"use client";

import type { HTMLAttributes, ReactNode } from "react";
import { GlassPillButton } from "../buttons/GlassPillButton";

export interface GlassTabPillProps<T extends string>
	extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
	/** Segmented options. When provided, the pill renders itself. */
	readonly tabs?: readonly T[];
	readonly selected?: T;
	readonly onSelect?: (value: T) => void;
	readonly label?: (value: T) => ReactNode;
}

/**
 * GlassTabPill — generic segmented pill control. When `tabs` is supplied it
 * renders a self-contained segmented control; otherwise it acts as a container
 * for manually placed `GlassPillButton` children (backward-compatible).
 */
export function GlassTabPill<T extends string>({
	tabs,
	selected,
	onSelect,
	label,
	children,
	className = "",
	...props
}: Readonly<GlassTabPillProps<T>>) {
	if (tabs !== undefined) {
		return (
			<div
				className={`ds-tab-pill ${className}`.trim()}
				role="tablist"
				{...props}
			>
				{tabs.map((value) => (
					<GlassPillButton
						key={value}
						selected={value === selected}
						onClick={onSelect !== undefined ? () => onSelect(value) : undefined}
						role="tab"
						aria-selected={value === selected}
					>
						{label !== undefined ? label(value) : value}
					</GlassPillButton>
				))}
			</div>
		);
	}
	return (
		<div
			className={`ds-tab-pill ${className}`.trim()}
			role="tablist"
			{...props}
		>
			{children}
		</div>
	);
}
